import type { FinishRunRequest, FinishRunResponse, ProgressRunResponse, StartRunResponse } from '@accra/shared';
import type { PlayerVehicleId } from '../content/playerVehicles';
import type { RunState } from '../game/RunState';
import { api } from './apiClient';
import { ensurePlayer, fetchMe, type Me } from './session';

const PENDING_KEY = 'trotrorun.pendingShifts.v1';

interface PendingShift {
  runId: string;
  report: FinishRunRequest;
}

function loadPending(): PendingShift[] {
  try {
    return JSON.parse(localStorage.getItem(PENDING_KEY) ?? '[]') as PendingShift[];
  } catch {
    return [];
  }
}

function savePending(list: PendingShift[]): void {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(list.slice(-20)));
  } catch {
    // Nothing we can do; the shift stays local only.
  }
}

export function reportFor(state: RunState, runToken: string, missions: FinishRunRequest['missionsCompleted']): FinishRunRequest {
  const { stats } = state;
  return {
    runToken,
    score: Math.floor(stats.score),
    metres: Math.floor(stats.metres),
    durationMs: Math.round(stats.seconds * 1000),
    faresPesewas: stats.faresPesewas,
    passengersCarried: stats.passengersCarried,
    passengersDropped: stats.passengersDropped,
    closeCalls: stats.closeCalls,
    perfectStops: stats.perfectStops,
    coins: stats.coins,
    offences: { redLight: stats.redLightsRun, pedestrianCrossing: stats.pedestrianOffences, rearEnd: stats.rearEnds, checkpoint: stats.checkpointsRun, papers: stats.papersPaid, dashTen: stats.dashTen, dashTwenty: stats.dashTwenty },
    fuelUnits: stats.fuelUnits,
    fuelStart: Math.round(state.fuel.start * 10) / 10,
    repairs: { ...stats.repairsByKind },
    endReason: state.endReason ?? 'endShift',
    ...(state.arrived ? { arrived: true } : {}),
    missionsCompleted: missions,
  };
}

/**
 * Talks to the backend about shifts: starts a run (for the anti-cheat token) and submits the result.
 * Everything is best-effort: offline shifts are queued and sent next time.
 */
export class ShiftSync {
  private playerKey: string | null = null;
  private current: Promise<{ runId: string; runToken: string } | null> = Promise.resolve(null);
  /** A progress update still on its way; the final result waits for it so the two never race. */
  private inFlight: Promise<unknown> = Promise.resolve();

  /** The key for other API calls (garage, name). Null until connected. */
  get key(): string | null {
    return this.playerKey;
  }

  /** Connects, flushes queued shifts, and returns the server's view of this player (the source of truth). */
  async connect(): Promise<Me | null> {
    const player = await ensurePlayer();
    if (!player) return null;
    this.playerKey = player.playerKey;
    await this.flushPending();
    return fetchMe(player.playerKey);
  }

  /** Starts a shift on the server. `options` asks for the daily challenge or a friend's challenge. */
  startShift(vehicle: PlayerVehicleId, route: string, options: { challenge?: 'daily'; friendCode?: string } = {}): void {
    const key = this.playerKey;
    this.current = key
      ? api<Omit<StartRunResponse, 'ok'>>('POST', '/runs', key, { vehicle, route, ...options }).then((r) => (r?.ok ? { runId: r.runId, runToken: r.runToken } : null))
      : Promise.resolve(null);
  }

  /** The id of the shift in progress (or just finished), for turning it into a challenge link. */
  async runId(): Promise<string | null> {
    return (await this.current)?.runId ?? null;
  }

  /**
   * Mid-shift update: the server credits what's new and updates the live leaderboard.
   * Best-effort: if it fails, the end-of-shift submission still covers everything.
   */
  async progress(state: RunState): Promise<ProgressRunResponse | null> {
    const run = await this.current;
    if (!run || !this.playerKey) return null;
    const { endReason, missionsCompleted, ...report } = reportFor(state, run.runToken, []);
    void endReason;
    void missionsCompleted;
    const request = api<Omit<ProgressRunResponse, 'ok'>>('POST', `/runs/${run.runId}/progress`, this.playerKey, report);
    this.inFlight = request;
    const result = await request;
    return result?.ok ? (result as ProgressRunResponse) : null;
  }

  /** Submits the finished shift. Resolves with the server's answer, or null if offline (queued for later). */
  async finishShift(state: RunState, missions: FinishRunRequest['missionsCompleted']): Promise<FinishRunResponse | null> {
    await this.inFlight.catch(() => undefined);
    const run = await this.current;
    if (!run || !this.playerKey) return null;
    const pending: PendingShift = { runId: run.runId, report: reportFor(state, run.runToken, missions) };
    const result = await this.submit(pending);
    if (result === 'retry') savePending([...loadPending(), pending]);
    return result === 'retry' || result === 'dropped' ? null : result;
  }

  private async submit(shift: PendingShift): Promise<FinishRunResponse | 'retry' | 'dropped'> {
    const result = await api<Omit<FinishRunResponse, 'ok'>>('POST', `/runs/${shift.runId}/finish`, this.playerKey, shift.report);
    if (!result) return 'retry';
    // Server said no (bad token, already sent): don't keep retrying it.
    if (!result.ok) return 'dropped';
    return result as FinishRunResponse;
  }

  private async flushPending(): Promise<void> {
    const pending = loadPending();
    if (pending.length === 0) return;
    const stillPending: PendingShift[] = [];
    for (const shift of pending) if ((await this.submit(shift)) === 'retry') stillPending.push(shift);
    savePending(stillPending);
  }
}
